// @flow

/* Libs */
import { useEffect, useState } from 'react';
import {
	getUnixTime, formatISO,
	isBefore, isToday, isTomorrow, startOfToday
} from 'date-fns';
import { format, addBusinessDays } from 'Helpers/dates';

/* Stores */
import useOrdersStore from 'Stores/Orders';
import useSnackBarStore from 'Stores/SnackBar';
import useModalStore from 'Stores/Modals';

/* Components */
import Loader from 'Components/Loader';
import Checkbox from 'Components/Checkbox';
import ContextualMenuWithIcon from 'Components/ContextualMenuWithIcon';
import OrderItem from './OrderItem';


/* Style */
import './Orders.scss';

const ListOrders = ({ sort }) => {
	// Categories
	const orders = useOrdersStore(store => store.orders);
	const fetchingOrders = useOrdersStore(store => store.fetchingOrders);
	const fetchOrders = useOrdersStore(store => store.fetchOrders);
	const updateManyStatus = useOrdersStore(store => store.updateManyStatus);
	const showSnackBar = useSnackBarStore(store => store.showSnackBar);
	const setActiveModal = useModalStore(store => store.setActiveModal);
	const [ordersSorted, setOrdersSorted] = useState(null);
	const [ordersSelected, setOrdersSelected] = useState([]);
	const [actions, setActions] = useState([]);
	const getTmp = ymd => getUnixTime(new Date(ymd));

	const [hasAllOrdersSelected, setHasAllOrdersSelected] = useState(false);

	useEffect(() => {
		if (orders && Object.keys(orders).length && orders[sort]) {
			const currentOrders = orders[sort] && orders[sort].datas;
			const newOrdersFormat = {};
			const formattedOrders = currentOrders.map((order) => {
				order.sendDate = formatISO(addBusinessDays(new Date(order.created_on), 3));
				return order;
			});

			formattedOrders.forEach((order) => {
				let formatDate = null;
				if (sort === 'sent') {
					formatDate = format(new Date(order.fulfilled_on || order.modified_on), 'yyyy-MM-dd');
				} else {
					formatDate = format(new Date(order.sendDate), 'yyyy-MM-dd');
				}
				const oldOrders = newOrdersFormat[formatDate] || [];
				newOrdersFormat[formatDate] = [...oldOrders, order];
			});

			Object.keys(newOrdersFormat).forEach((date) => {
				if (sort === 'sent') {
					newOrdersFormat[date] = newOrdersFormat[date].sort((a, b) => getTmp(b.fulfilled_on || b.modified_on) - getTmp(a.fulfilled_on || b.modified_on));
				} else {
					newOrdersFormat[date] = newOrdersFormat[date].sort((a, b) => getTmp(a.created_on) - getTmp(b.created_on));
				}
			});

			setOrdersSorted(newOrdersFormat);

			/* Contextual menu actions */
			const orderActions = [
				{
					label: 'Remettre à nouveau',
					value: 'new'
				},
				{
					label: 'À Préparer',
					value: 'pending'
				},
				{
					label: 'À Envoyer',
					value: 'sending'
				},
				{
					label: 'Terminé',
					value: 'sent'
				}
			];

			const actionsFiltered = orderActions.filter(action => action.value !== sort);
			setActions(actionsFiltered);
		}

		if (!fetchingOrders && !orders[sort]) {
			setOrdersSorted(null);
			fetchOrders(sort);
		}

		if (sort) {
			setOrdersSelected([]);
		}
	}, [orders, sort]);

	const selectAllOrders = (newValue) => {
		setHasAllOrdersSelected(newValue);

		if (!newValue) {
			setOrdersSelected([]);
		} else {
			const allOrders = Object.values(ordersSorted).flat();
			setOrdersSelected(allOrders);
		}
	};

	useEffect(() => {
		if (ordersSorted) {
			const allOrders = Object.values(ordersSorted).flat();
			setHasAllOrdersSelected(allOrders.length > 0 && ordersSelected?.length === allOrders.length);
		}
	}, [ordersSelected, ordersSorted]);

	const selectedOrdersNewStatus = status => {
		// updateStatus(order, status);
		if (ordersSelected.length > 1 && status === 'sent') {
			showSnackBar({ theme: 'danger', message: 'Impossible de terminer plusieurs commandes en même temps' });
		} else if (ordersSelected.length === 1 && status === 'sent') {
			setActiveModal('sentOrder', { order: ordersSelected[0] });
			setOrdersSelected([]);
		} else {
			updateManyStatus(ordersSelected, status);
			setOrdersSelected([]);
		}
	};

	const handleSelectedOrder = order => {
		const existingOrder = ordersSelected.find(o => o.id === order.id);

		if (!existingOrder) {
			setOrdersSelected([...ordersSelected, order]);
		} else {
			setOrdersSelected([...ordersSelected.filter(o => o.id !== order.id)]);
		}
	};

	const sortOrdersFunction = (a, b) => {
		const aDate = getUnixTime(new Date(a));
		const bDate = getUnixTime(new Date(b));

		if (sort === 'sent') {
			return bDate - aDate;
		}

		return aDate - bDate;
	};

	const getSendDate = (d) => {
		const date = new Date(d);

		if (isToday(date)) {
			return <p>À envoyer aujourd&apos;hui</p>;
		}
		if (isTomorrow(date)) {
			return <p>À envoyer demain</p>;
		}

		if (isBefore(date, startOfToday())) {
			return <p>En retard - Pour le { format(new Date(date), 'PPP') }</p>;
		}

		return <p>Envoi d&apos;ici le { format(new Date(date), 'PPP') }</p>;
	};

	return (
		<div className="App-Orders-listing">
			<div className="orders-list">
				{ !ordersSorted && <Loader /> }
				{ ordersSorted && Object.keys(ordersSorted).length === 0 && (
					<div className="empty-order">
						<img src="https://img.icons8.com/cotton/2x/empty-box.png" alt="no command" />
						<p>Aucune commande trouvée</p>
					</div>
				)}
				{ ordersSorted && Object.keys(ordersSorted).length > 0 && (
					<div className="filter-select">
						<div
							className="selector"
							onClick={e => e.stopPropagation()}
							role="button"
							tabIndex="0"
						>
							<Checkbox
								action={() => selectAllOrders(!hasAllOrdersSelected)}
								choice={{
									value: hasAllOrdersSelected,
									label: 'Sélectionner toutes les commandes'
								}}
							/>
						</div>
					</div>
				)}
				{ ordersSorted && Object.keys(ordersSorted).length > 0 && Object.keys(ordersSorted).sort(sortOrdersFunction).map(date => (
					<div className="orders-bysenddate" key={date}>
						<div className="orders-header">
							{ sort === 'sent' && (
								<p>Terminé le { format(new Date(date), 'PPP') }</p>
							)}
							{ sort !== 'sent' && getSendDate(date)}
							<span className="orders-number">{ordersSorted[date].length}</span>
						</div>
						{ ordersSorted[date].map(order => (
							<OrderItem
								key={order.id}
								isSelected={ordersSelected.find(o => o.id === order.id) !== undefined}
								handleSelectedOrder={handleSelectedOrder}
								order={order}
								statusActions={actions}
							/>
						))}
					</div>
				))}
			</div>
			{ ordersSelected && ordersSelected.length > 0 && (
				<div className="Orders-selected">
					<p><span>{ ordersSelected.length }</span> commande(s) sélectionnée(s)</p>
					<div className="actions">
						<ContextualMenuWithIcon
							button="Modifier"
							handleAction={selectedOrdersNewStatus}
							items={actions}
							isTop
						/>
					</div>
				</div>
			)}
		</div>
	);
};

ListOrders.propTypes = {
	sort: PropTypes.string.isRequired
};

export default ListOrders;
